/**
 * RUBYN — central runtime/site configuration.
 *
 * Everything that differs between environments (web vs Capacitor app,
 * local vs production) is driven from here so we never hardcode hosts.
 *
 * Build target is selected at build time via `BUILD_TARGET`:
 *   - (unset) | "web"  → SSR/ISR build for Vercel (default)
 *   - "app"            → static export (`output: 'export'`) wrapped by Capacitor
 *
 * Public values must be prefixed `NEXT_PUBLIC_` to reach the browser bundle.
 */

export type BuildTarget = "web" | "app";

export const BUILD_TARGET: BuildTarget =
  process.env.BUILD_TARGET === "app" ? "app" : "web";

export const IS_APP_BUILD = BUILD_TARGET === "app";
export const IS_WEB_BUILD = BUILD_TARGET === "web";

/** Canonical production domain (used for canonical URLs, sitemap, OG). */
export const SITE_DOMAIN =
  process.env.NEXT_PUBLIC_SITE_DOMAIN ?? "rubynfashion.it";

export const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL ?? `https://${SITE_DOMAIN}`;

/**
 * Backend API base, including the `/api/v1` prefix.
 *
 * Always the **public** URL, never `127.0.0.1:8100`: these requests are made by
 * the browser, and inside the Capacitor app there is no localhost backend to
 * reach at all. Caddy terminates TLS on `rubynparis.it` and proxies `/api/v1/*`
 * to the backend on 8100 (see /root/alpha/.caddy/Caddyfile); the prefix is
 * `/api/v1` and not `/api` so Next keeps `/api/*` free for its own route
 * handlers.
 *
 * Not derived from SITE_URL on purpose — that one is the canonical SEO domain
 * and the two are not the same host today.
 */
export const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "https://rubynparis.it/api/v1";

export const BRAND = {
  name: "RUBYN",
  legalName: "RUBYN®",
  tagline: "By women, for women.",
  email: "hello@rubynfashion.it",
  legalEmail: "legal@rubynfashion.it",
} as const;

/** Default SEO copy (matches the old index.html). */
export const SEO_DEFAULTS = {
  title: "Best Beauty & Fashion Marketplace | RUBYN",
  titleTemplate: "%s | RUBYN",
  description:
    "RUBYN is the largest international marketplace for women’s beauty, perfumes, and fashion. Discover top brands and exclusive deals.",
} as const;

/** Default OpenGraph/Twitter share image (overridable via env). */
export const OG_IMAGE =
  process.env.NEXT_PUBLIC_OG_IMAGE ?? "https://rysev7.it:3077/media/rubynog.jpg";

/** Favicon / app icon. */
export const FAVICON = "/rubyn-metal-mobile-logo-little.png";

export const SITE = {
  domain: SITE_DOMAIN,
  url: SITE_URL,
  apiBaseUrl: API_BASE_URL,
  buildTarget: BUILD_TARGET,
  isApp: IS_APP_BUILD,
} as const;
